import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useParallax } from '../../../hooks/useParallax';

export default function Consequences() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });
  
  // Parallax effects
  const parallax1 = useParallax({ speed: 0.4, direction: 'down' });
  const parallax2 = useParallax({ speed: 0.3, direction: 'up' });
  const parallax3 = useParallax({ speed: 0.5, direction: 'down' });
  const consequences = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Prejuízo financeiro direto',
      description: 'Pagamento por horas não trabalhadas que somam milhares por mês',
      stat: 'Até 30% de custo extra',
      color: 'from-red-600 to-rose-600',
      bgGlow: 'group-hover:shadow-red-500/50'
    },
    {
      icon: 'ri-team-line',
      title: 'Falta de alinhamento organizacional',
      description: 'Desconfiança entre gestores e equipe, conflitos constantes',
      stat: 'Aumento de rotatividade',
      color: 'from-orange-600 to-amber-600',
      bgGlow: 'group-hover:shadow-orange-500/50'
    },
    {
      icon: 'ri-time-line',
      title: 'Tempo perdido todo mês',
      description: 'Horas gastas tentando entender o que realmente aconteceu',
      stat: 'Dias de trabalho perdidos',
      color: 'from-amber-600 to-yellow-600',
      bgGlow: 'group-hover:shadow-amber-500/50'
    },
    {
      icon: 'ri-alert-line',
      title: 'Risco jurídico crescente',
      description: 'Processos trabalhistas por falta de documentação adequada',
      stat: 'Multas e indenizações',
      color: 'from-rose-600 to-red-600',
      bgGlow: 'group-hover:shadow-rose-500/50'
    }
  ];

  return (
    <section 
      id="consequencias"
      ref={ref}
      className={`py-20 lg:py-28 bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 relative overflow-hidden transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
    >
      <div className="absolute inset-0 opacity-30">
        <div 
          className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-red-500 to-rose-500 rounded-full blur-3xl animate-pulse"
          style={{ transform: `translateY(${parallax1}px)` }}
        ></div>
        <div 
          className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-br from-orange-500 to-amber-500 rounded-full blur-3xl animate-pulse" 
          style={{ animationDelay: '1.5s', transform: `translateY(${parallax2}px)` }}
        ></div>
        <div 
          className="absolute top-1/2 left-1/2 w-80 h-80 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full blur-3xl animate-pulse" 
          style={{ animationDelay: '3s', transform: `translateY(${parallax3}px)` }}
        ></div>
      </div>

      {/* Padrão decorativo com parallax */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="absolute top-20 right-20 w-32 h-32 border-4 border-red-500 rounded-full"
          style={{ transform: `translateY(${parallax1 * 0.6}px)` }}
        ></div>
        <div 
          className="absolute bottom-40 left-20 w-24 h-24 border-4 border-orange-500 rounded-lg rotate-45"
          style={{ transform: `translateY(${parallax2 * 0.7}px) rotate(45deg)` }}
        ></div>
        <div 
          className="absolute top-1/3 left-1/4 w-40 h-40 border-4 border-amber-500 rounded-full"
          style={{ transform: `translateY(${parallax3 * 0.5}px)` }}
        ></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block mb-4 relative">
            <span className="relative bg-gradient-to-r from-red-600 via-orange-500 to-red-600 text-white px-8 py-3 rounded-full text-sm font-bold shadow-2xl inline-flex items-center gap-2 transform hover:scale-110 transition-all duration-300 cursor-pointer group overflow-hidden animate-pulse">
              {/* Efeito de brilho pulsante */}
              <div className="absolute inset-0 bg-gradient-to-r from-orange-600 via-red-500 to-orange-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
              
              {/* Ondas de alerta */}
              <div className="absolute inset-0 rounded-full animate-ping bg-red-500 opacity-75"></div>
              <div className="absolute inset-0 rounded-full animate-pulse bg-orange-500 opacity-50"></div>
              
              {/* Ícone de alerta piscante */}
              <i className="ri-error-warning-line text-xl relative z-10 animate-bounce"></i>
              
              <span className="relative z-10 tracking-wide">CONSEQUÊNCIAS GRAVES</span>
              
              {/* Pontos de alerta piscantes */}
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping"></div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full"></div>
              <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
              <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-yellow-400 rounded-full"></div>
            </span>
            
            {/* Círculos de alerta ao redor */}
            <div className="absolute -inset-4 border-2 border-red-500 rounded-full animate-ping opacity-50"></div>
            <div className="absolute -inset-6 border-2 border-orange-500 rounded-full animate-ping opacity-30" style={{ animationDelay: '0.3s' }}></div>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            O custo real de não resolver isso
          </h2>
          <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Cada dia sem controle adequado representa perdas que se acumulam
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {consequences.map((consequence, index) => (
            <div
              key={index}
              className={`group relative bg-white p-8 rounded-2xl shadow-xl hover:shadow-3xl ${consequence.bgGlow} transition-all duration-300 transform hover:-translate-y-3 cursor-pointer overflow-hidden border-2 border-transparent hover:border-red-200`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${consequence.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
              
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-br ${consequence.color} rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-xl`}>
                    <i className={`${consequence.icon} text-3xl text-white`}></i>
                  </div>
                  <span className={`text-xs font-bold px-4 py-2 rounded-full bg-gradient-to-r ${consequence.color} text-white shadow-lg transform group-hover:scale-105 transition-transform`}>
                    {consequence.stat}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors leading-snug">
                  {consequence.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">{consequence.description}</p>
              </div>

              <div className={`absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r ${consequence.color} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-b-2xl`}></div>
              
              <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${consequence.color} opacity-10 rounded-full transform translate-x-12 -translate-y-12 group-hover:scale-150 transition-transform duration-500`}></div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}